import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, vModelSelect as _vModelSelect, withDirectives as _withDirectives, vModelText as _vModelText, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "h-full w-full overflow-y-auto p-6 bg-slate-50 dark:bg-slate-900/50 flex flex-col gap-6" }
const _hoisted_2 = { class: "bg-card border rounded-lg p-4 shadow-sm flex flex-col gap-4" }
const _hoisted_3 = { class: "font-semibold text-lg border-b pb-2 flex items-center gap-2" }
const _hoisted_4 = {
  key: 0,
  class: "text-center text-muted-foreground py-4 text-xs border border-dashed rounded"
}
const _hoisted_5 = { class: "flex flex-col gap-2" }
const _hoisted_6 = { class: "flex flex-col gap-1 items-center justify-center shrink-0 w-8" }
const _hoisted_7 = ["onClick", "disabled"]
const _hoisted_8 = { class: "text-xs font-mono font-bold" }
const _hoisted_9 = ["onClick", "disabled"]
const _hoisted_10 = { class: "flex flex-col gap-1 w-32 shrink-0" }
const _hoisted_11 = ["onUpdate:modelValue"]
const _hoisted_12 = { class: "flex flex-col gap-1 flex-1" }
const _hoisted_13 = ["onUpdate:modelValue"]
const _hoisted_14 = ["onClick"]
const _hoisted_15 = { class: "text-sm font-bold p-2 bg-muted-foreground/10 text-muted-foreground flex items-center justify-between border-b shrink-0" }

import { ref, watch } from 'vue'
import { Button } from '@/components/ui/button'
import { Plus, Trash2, RefreshCw, LayoutTemplate } from 'lucide-vue-next'
import BaseVisualEditor from './BaseVisualEditor.vue'
import MermaidCodeEditor from './MermaidCodeEditor.vue'
import MermaidLivePreview from './MermaidLivePreview.vue'

type EMType = 'ui' | 'cmd' | 'evt' | 'view'

interface EMItem {
  id: string
  type: EMType
  name: string
}


export default /*@__PURE__*/_defineComponent({
  __name: 'EventModelingEditorDialog',
  props: {
    open: { type: Boolean }
  },
  emits: ["update:open", "save"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

const showCode = ref(true)
const copied = ref(false)
const editableCode = ref('')

const items = ref<EMItem[]>([
  { id: crypto.randomUUID(), type: 'ui', name: 'UserUI' },
  { id: crypto.randomUUID(), type: 'cmd', name: 'RegisterUser' },
  { id: crypto.randomUUID(), type: 'evt', name: 'UserRegistered' },
  { id: crypto.randomUUID(), type: 'view', name: 'UserList' }
])

const generateId = () => crypto.randomUUID()

const addItem = () => {
  items.value.push({
    id: generateId(),
    type: 'evt',
    name: `NewEvent`
  })
}

const removeItem = (idx: number) => {
  items.value.splice(idx, 1)
}

const moveItem = (idx: number, dir: 1 | -1) => {
  if (idx + dir < 0 || idx + dir >= items.value.length) return
  const temp = items.value[idx]
  items.value[idx] = items.value[idx + dir]
  items.value[idx + dir] = temp
}

const generateMermaid = () => {
  let code = `eventmodeling\n`
  
  items.value.forEach((item, index) => {
    const tf = (index + 1).toString().padStart(2, '0')
    const safeName = item.name.replace(/[^a-zA-Z0-9_]/g, '') || `Item${tf}`
    code += `  timeframe ${tf} ${item.type} ${safeName}\n`
  })
  
  editableCode.value = code
}

const syncToVisual = () => {
  const code = editableCode.value
  const lines = code.split('\n')
  const newItems: EMItem[] = []
  
  const tfRegex = /^(?:tf|timeframe)\s+([a-zA-Z0-9_]+)\s+(ui|cmd|evt|view)\s+(.*)$/
  
  for (const line of lines) {
    const t = line.trim()
    if (tfRegex.test(t)) {
      const match = t.match(tfRegex)!
      newItems.push({
        id: generateId(),
        type: match[2] as EMType,
        name: match[3].trim()
      })
    }
  }
  
  if (newItems.length > 0) {
    items.value = newItems
  }
}

watch(() => props.open, (newVal) => {
  if (newVal) {
    const editingCode = localStorage.getItem('editingDiagramCode');
    if (editingCode) {
      setTimeout(() => {
        editableCode.value = editingCode;
        localStorage.removeItem('editingDiagramCode');
        syncToVisual();
      }, 300);
      return;
    }
  }

  if (newVal && !editableCode.value) {
    generateMermaid()
  }
}, { immediate: true })

watch(items, () => {
  generateMermaid()
}, { deep: true })

const copyToClipboard = async () => {
  try {
    await navigator.clipboard.writeText(editableCode.value)
    copied.value = true
    setTimeout(() => copied.value = false, 2000)
  } catch (err) {
    console.error('Failed to copy text: ', err)
  }
}

const saveAndClose = () => {
  emit('save', editableCode.value)
  emit('update:open', false)
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(BaseVisualEditor, {
    open: __props.open,
    "onUpdate:open": _cache[1] || (_cache[1] = ($event: any) => (_ctx.$emit('update:open', $event))),
    title: "Event Modeling Diagram",
    description: "Diseña el flujo de información y estados del sistema sobre una línea de tiempo (Timeframes).",
    showCode: showCode.value,
    "onUpdate:showCode": _cache[2] || (_cache[2] = ($event: any) => (showCode.value = $event)),
    copied: copied.value,
    onCopy: copyToClipboard,
    onSave: saveAndClose
  }, {
    icon: _withCtx(() => [
      _createVNode(_unref(LayoutTemplate), { class: "w-5 h-5 text-primary" })
    ]),
    toolbar: _withCtx(() => [
      _createVNode(_unref(Button), {
        size: "sm",
        variant: "default",
        onClick: addItem
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(Plus), { class: "w-4 h-4 mr-2" }),
          _cache[3] || (_cache[3] = _createTextVNode(" Añadir Paso (Timeframe)", -1))
        ]),
        _: 1
      })
    ]),
    canvas: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("h3", _hoisted_3, [
            _createVNode(_unref(LayoutTemplate), { class: "w-5 h-5 text-blue-500" }),
            _cache[4] || (_cache[4] = _createTextVNode(" Línea de Tiempo (Timeframes) ", -1))
          ]),
          (items.value.length === 0)
            ? (_openBlock(), _createElementBlock("div", _hoisted_4, " No hay elementos. Añade tu primer paso (Timeframe). "))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_5, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(items.value, (item, idx) => {
              return (_openBlock(), _createElementBlock("div", {
                key: item.id,
                class: "flex items-center gap-3 bg-muted/40 border p-2 rounded-md group hover:bg-muted/60 transition-colors"
              }, [
                _createElementVNode("div", _hoisted_6, [
                  _createElementVNode("button", {
                    onClick: ($event: any) => (moveItem(idx, -1)),
                    disabled: idx === 0,
                    class: "text-muted-foreground hover:text-primary disabled:opacity-30"
                  }, " ▲ ", 8, _hoisted_7),
                  _createElementVNode("div", _hoisted_8, _toDisplayString((idx + 1).toString().padStart(2, '0')), 1),
                  _createElementVNode("button", {
                    onClick: ($event: any) => (moveItem(idx, 1)),
                    disabled: idx === items.value.length - 1,
                    class: "text-muted-foreground hover:text-primary disabled:opacity-30"
                  }, " ▼ ", 8, _hoisted_9)
                ]),
                _createElementVNode("div", _hoisted_10, [
                  _cache[6] || (_cache[6] = _createElementVNode("label", { class: "text-[10px] font-bold uppercase text-muted-foreground ml-1" }, "Tipo", -1)),
                  _withDirectives(_createElementVNode("select", {
                    "onUpdate:modelValue": ($event: any) => ((item.type) = $event),
                    class: "bg-background border rounded px-2 py-1 text-sm focus:outline-none focus:ring-1"
                  }, [...(_cache[5] || (_cache[5] = [
                    _createElementVNode("option", { value: "ui" }, "UI (Interfaz)", -1),
                    _createElementVNode("option", { value: "cmd" }, "Comando (Acción)", -1),
                    _createElementVNode("option", { value: "evt" }, "Evento (Estado)", -1),
                    _createElementVNode("option", { value: "view" }, "Vista (Read Model)", -1)
                  ]))], 8, _hoisted_11), [
                    [_vModelSelect, item.type]
                  ])
                ]),
                _createElementVNode("div", _hoisted_12, [
                  _cache[7] || (_cache[7] = _createElementVNode("label", { class: "text-[10px] font-bold uppercase text-muted-foreground ml-1" }, "Nombre (Sin espacios)", -1)),
                  _withDirectives(_createElementVNode("input", {
                    "onUpdate:modelValue": ($event: any) => ((item.name) = $event),
                    class: "bg-background border rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 font-mono",
                    placeholder: "Ej. UserRegistered"
                  }, null, 8, _hoisted_13), [
                    [_vModelText, item.name]
                  ])
                ]),
                _createElementVNode("button", {
                  onClick: ($event: any) => (removeItem(idx)),
                  class: "text-muted-foreground hover:bg-destructive hover:text-white p-2 rounded transition-colors",
                  title: "Eliminar Paso"
                }, [
                  _createVNode(_unref(Trash2), { class: "w-4 h-4" })
                ], 8, _hoisted_14)
              ]))
            }), 128))
          ])
        ])
      ])
    ]),
    "code-panel": _withCtx(() => [
      _createElementVNode("div", _hoisted_15, [
        _cache[9] || (_cache[9] = _createTextVNode(" Mermaid (Sincronizado) ", -1)),
        _createVNode(_unref(Button), {
          size: "sm",
          variant: "default",
          class: "h-7 text-xs",
          onClick: syncToVisual
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(RefreshCw), { class: "w-3 h-3 mr-2" }),
            _cache[8] || (_cache[8] = _createTextVNode(" Sincronizar a Visual ", -1))
          ]),
          _: 1
        })
      ]),
      _createVNode(MermaidCodeEditor, {
        modelValue: editableCode.value,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((editableCode).value = $event)),
        class: "flex-1 min-h-0"
      }, null, 8, ["modelValue"])
    ]),
    "live-preview": _withCtx(() => [
      _createVNode(MermaidLivePreview, { code: editableCode.value }, null, 8, ["code"])
    ]),
    _: 1
  }, 8, ["open", "showCode", "copied"]))
}
}

})